import { useState } from 'react';
import type { Contract } from '../../../../types';
import { updateContract } from '../../../../store';
import { showToast } from '../../../../components/base/Toast';
import { useModalBackHandler } from '../../../../hooks/useModalBackHandler';
import { CropSelect } from '@/components/feature/CropSelect';

interface EditContractModalProps {
  contract: Contract;
  htxId?: string;
  onClose: () => void;
  onUpdated: (contract: Contract) => void;
}

const digitsOnly = (value: string) => value.replace(/\D/g, '');
const formatMoneyInput = (value: string) => digitsOnly(value).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
const parseMoneyInput = (value: string) => Number(digitsOnly(value) || 0);

/** Only the fields the backend's UpdateContractDraftDto actually accepts —
 * farmerId/businessType/templateId/parties/effectiveDate are immutable once a
 * contract is drafted (no update route covers them). */
export function EditContractModal({ contract, htxId, onClose, onUpdated }: EditContractModalProps) {
  useModalBackHandler(true, onClose);

  const [counterpartyName, setCounterpartyName] = useState(contract.doiTac || '');
  const [expiryDate, setExpiryDate] = useState(contract.ngayHetHan || '');
  const [valueVnd, setValueVnd] = useState(contract.giaTri ? formatMoneyInput(String(contract.giaTri)) : '');
  const [mainCrop, setMainCrop] = useState(contract.cayTrong || '');
  const [variety, setVariety] = useState(contract.giong || '');
  const [areaHa, setAreaHa] = useState(contract.dienTich !== undefined ? String(contract.dienTich) : '');
  const [expectedOutputKg, setExpectedOutputKg] = useState(contract.sanLuongDuKien !== undefined ? String(contract.sanLuongDuKien) : '');
  const [expectedUnitPriceVnd, setExpectedUnitPriceVnd] = useState(contract.donGiaDuKien ? formatMoneyInput(String(contract.donGiaDuKien)) : '');
  const [expectedDeliveryDate, setExpectedDeliveryDate] = useState(contract.ngayGiaoHangDuKien || '');
  const [notes, setNotes] = useState(contract.ghiChu || '');
  const [submitting, setSubmitting] = useState(false);

  async function handleSubmit() {
    setSubmitting(true);
    try {
      const updated = await updateContract(
        contract.id,
        {
          counterpartyName: counterpartyName.trim() || undefined,
          expiryDate: expiryDate || undefined,
          valueVnd: valueVnd ? parseMoneyInput(valueVnd) : undefined,
          mainCrop: mainCrop.trim() || undefined,
          variety: variety.trim() || undefined,
          areaHa: areaHa ? Number(areaHa) : undefined,
          expectedOutputKg: expectedOutputKg ? Number(expectedOutputKg) : undefined,
          expectedUnitPriceVnd: expectedUnitPriceVnd ? parseMoneyInput(expectedUnitPriceVnd) : undefined,
          expectedDeliveryDate: expectedDeliveryDate || undefined,
          notes: notes.trim() || undefined,
        },
        contract.farmerId,
        htxId,
      );
      showToast('Đã cập nhật hợp đồng', 'success');
      onUpdated(updated);
    } catch (e) {
      showToast(e instanceof Error ? e.message : 'Cập nhật hợp đồng thất bại', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="fixed inset-0 z-[90] flex items-end sm:items-center justify-center p-0 sm:p-4">
      <div className="absolute inset-0 bg-black/40 animate-modal-backdrop" onClick={onClose} />
      <div onClick={(e) => e.stopPropagation()} className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto animate-modal-slide-up">
        <div className="sticky top-0 bg-white border-b border-slate-100 px-4 py-3 flex items-center justify-between z-10">
          <h3 className="text-base font-semibold text-slate-900">Sửa hợp đồng {contract.soHopDong}</h3>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-400">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="p-4 space-y-3">
          {contract.loaiHopDong !== 'HTX-Nông hộ' && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Đối tác</label>
              <input type="text" value={counterpartyName} onChange={(e) => setCounterpartyName(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ngày hết hạn</label>
            <input type="date" value={expiryDate} onChange={(e) => setExpiryDate(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Giá trị hợp đồng (VNĐ)</label>
            <input type="text" inputMode="numeric" value={valueVnd} onChange={(e) => setValueVnd(formatMoneyInput(e.target.value))} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cây trồng</label>
              <CropSelect value={mainCrop} onChange={(name) => setMainCrop(name)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-agrida-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Giống</label>
              <input type="text" value={variety} onChange={(e) => setVariety(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Diện tích (ha)</label>
              <input type="number" value={areaHa} onChange={(e) => setAreaHa(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Sản lượng dự kiến (kg)</label>
              <input type="number" value={expectedOutputKg} onChange={(e) => setExpectedOutputKg(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Đơn giá dự kiến (VNĐ/kg)</label>
              <input type="text" inputMode="numeric" value={expectedUnitPriceVnd} onChange={(e) => setExpectedUnitPriceVnd(formatMoneyInput(e.target.value))} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Ngày giao hàng dự kiến</label>
              <input type="date" value={expectedDeliveryDate} onChange={(e) => setExpectedDeliveryDate(e.target.value)} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Ghi chú</label>
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-agrida-500" />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-slate-100 px-4 py-3 flex gap-3 justify-end">
          <button type="button" onClick={onClose} className="px-4 py-2.5 rounded-xl text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors whitespace-nowrap min-h-[44px]">Hủy</button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            className="px-4 py-2.5 rounded-xl text-sm font-medium text-white bg-agrida-700 hover:bg-agrida-800 disabled:opacity-50 transition-colors whitespace-nowrap min-h-[44px]"
          >
            {submitting ? 'Đang lưu...' : 'Lưu thay đổi'}
          </button>
        </div>
      </div>
    </div>
  );
}
